// pages/Agents/Workflows/WorkflowBuilder.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, IconButton, Card, CardContent,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Accordion, AccordionSummary, AccordionDetails, Chip, Grid,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import { Alert } from "@mui/material";
import axiosInstance from "../../../utils/axiosInstance";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TiptapEditor from "../../../components/Common/TiptapEditor";
import { getEmailBodyForAction } from "./workflowTemplates";

// Wrapper component for Tiptap body editor (hooks can't be called in loops)
function BodyEditorField({ value, onChange, label }) {
  const editor = useEditor({
    extensions: [StarterKit, Underline],
    content: value || "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Sync external value changes (e.g. template applied)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
        {label}
      </Typography>
      <Box sx={{ border: "1px solid #ccc", borderRadius: 1, overflow: "hidden" }}>
        <TiptapEditor editor={editor} height="150px" />
      </Box>
      <Typography variant="caption" color="text.secondary">
        Use {"{{prev_output.field}}"} to reference previous step output. Email body is auto-populated based on the previous step.
      </Typography>
    </Box>
  );
}

// Map parameter names to resource types for dynamic fetching
const DYNAMIC_PARAM_RESOURCES = {
  survey_id: 'surveys',
  event_id: 'events',
  volunteer_id: 'volunteers',
  contact_id: 'contacts',
  lead_id: 'leads',
  opportunity_id: 'opportunities',
  course_id: 'courses',
  student_id: 'students',
};

// Map email action types to agent names and setup routes
const EMAIL_AGENT_MAP = {
  "comm.send_email_gmail": { agent: "GMAIL_EMAIL", label: "Gmail", path: "gmail-email" },
  "comm.send_email_outlook": { agent: "OUTLOOK_EMAIL", label: "Outlook", path: "outlook-email" },
};

export default function WorkflowBuilder({ steps, setSteps, actions, chatbotId }) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(null);
  const [dynamicOptions, setDynamicOptions] = useState({});
  const [loadingOptions, setLoadingOptions] = useState({});
  const [agentStatus, setAgentStatus] = useState({});

  // Fetch agent status to check email configuration
  useEffect(() => {
    const fetchAgentStatus = async () => {
      try {
        const params = chatbotId ? { chatbot_id: chatbotId } : {};
        const res = await axiosInstance.get("/api/agents/status", { params });
        setAgentStatus(res.data.agents || {});
      } catch (err) {
        console.error("Failed to fetch agent status");
      }
    };
    fetchAgentStatus();
  }, [chatbotId]);

  const addStep = () => {
    setSteps((prev) => [
      ...prev,
      {
        step_order: prev.length,
        name: "",
        action_type: "",
        action_config: {},
        condition: null,
      },
    ]);
    setExpanded(steps.length);
  };

  const updateStep = (index, field, value) => {
    setSteps((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const removeStep = (index) => {
    setSteps((prev) => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, step_order: i })));
    setExpanded(null);
  };

  const moveStep = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= steps.length) return;
    setSteps((prev) => {
      const updated = [...prev];
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
      return updated.map((s, i) => ({ ...s, step_order: i }));
    });
  };

  const getActionSchema = (actionType) => {
    const action = actions.find((a) => a.action_type === actionType);
    return action?.schema?.properties || {};
  };

  const getActionLabel = (actionType) => {
    const action = actions.find((a) => a.action_type === actionType);
    return action?.label || actionType;
  };

  // Check if a parameter should use dynamic options
  const isDynamicParam = (paramName) => {
    return DYNAMIC_PARAM_RESOURCES.hasOwnProperty(paramName);
  };

  // Get resource type for a parameter
  const getResourceType = (paramName) => {
    return DYNAMIC_PARAM_RESOURCES[paramName];
  };

  // Fetch dynamic options for a resource type
  const fetchDynamicOptions = useCallback(async (resource) => {
    if (!chatbotId) return;

    setLoadingOptions((prev) => ({ ...prev, [resource]: true }));
    try {
      const res = await axiosInstance.get("/api/workflows/actions/options", {
        params: { chatbot_id: chatbotId, resource },
      });
      setDynamicOptions((prev) => ({ ...prev, [resource]: res.data.data || [] }));
    } catch (err) {
      console.error(`Failed to fetch ${resource} options:`, err);
      setDynamicOptions((prev) => ({ ...prev, [resource]: [] }));
    } finally {
      setLoadingOptions((prev) => ({ ...prev, [resource]: false }));
    }
  }, [chatbotId]);

  // Fetch options when a step with dynamic params is expanded
  useEffect(() => {
    if (expanded !== null && steps[expanded]) {
      const step = steps[expanded];
      if (step.action_type) {
        const action = actions.find((a) => a.action_type === step.action_type);
        const schema = action?.schema?.properties || {};
        Object.keys(schema).forEach((paramName) => {
          if (isDynamicParam(paramName)) {
            const resource = getResourceType(paramName);
            setDynamicOptions((prev) => {
              if (!prev[resource]) {
                fetchDynamicOptions(resource);
              }
              return prev;
            });
          }
        });
      }
    }
  }, [expanded, steps, actions, fetchDynamicOptions]);

  // Group actions by category
  const actionsByCategory = actions.reduce((acc, a) => {
    if (!acc[a.category]) acc[a.category] = [];
    acc[a.category].push(a);
    return acc;
  }, {});

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Workflow Steps ({steps.length})
      </Typography>

      {steps.map((step, index) => (
        <Accordion
          key={index}
          expanded={expanded === index}
          onChange={() => setExpanded(expanded === index ? null : index)}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
              <DragIndicatorIcon color="action" fontSize="small" />
              <Chip label={`Step ${index + 1}`} size="small" color="primary" variant="outlined" />
              <Typography variant="body2" fontWeight={500}>
                {step.name || getActionLabel(step.action_type) || "Unconfigured step"}
              </Typography>
              {step.action_type && (
                <Chip label={step.action_type} size="small" variant="outlined" sx={{ ml: "auto", mr: 2 }} />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Step Name (optional)"
                  value={step.name || ""}
                  onChange={(e) => updateStep(index, "name", e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={step.action_type || ""}
                    label="Action"
                    onChange={(e) => {
                      const newAction = e.target.value;
                      updateStep(index, "action_type", newAction);
                      // Auto-populate email body based on previous step's action
                      const isEmail = newAction === "comm.send_email_gmail" || newAction === "comm.send_email_outlook";
                      if (isEmail && index > 0) {
                        const prevAction = steps[index - 1]?.action_type;
                        const autoBody = getEmailBodyForAction(prevAction);
                        updateStep(index, "action_config", { body: autoBody });
                      } else {
                        updateStep(index, "action_config", {});
                      }
                    }}
                  >
                    {Object.entries(actionsByCategory).map(([category, categoryActions]) => [
                      <MenuItem key={`cat-${category}`} disabled sx={{ fontWeight: 700, opacity: 1 }}>
                        {category}
                      </MenuItem>,
                      ...categoryActions.map((a) => (
                        <MenuItem key={a.action_type} value={a.action_type} sx={{ pl: 4 }}>
                          {a.label}
                        </MenuItem>
                      )),
                    ])}
                  </Select>
                </FormControl>
              </Grid>

              {/* Email agent setup check */}
              {step.action_type && EMAIL_AGENT_MAP[step.action_type] && (() => {
                const cfg = EMAIL_AGENT_MAP[step.action_type];
                const agent = agentStatus[cfg.agent];
                if (!agent || !agent.is_configured) {
                  return (
                    <Grid item xs={12}>
                      <Alert
                        severity="warning"
                        action={
                          <Button
                            color="warning"
                            size="small"
                            onClick={() => navigate(`/chatbot/${chatbotId}/agents/${cfg.path}`)}
                          >
                            Setup {cfg.label}
                          </Button>
                        }
                      >
                        Please setup {cfg.label} first to use this action.
                      </Alert>
                    </Grid>
                  );
                }
                return null;
              })()}

              {/* Dynamic parameter fields from schema */}
              {step.action_type &&
                Object.entries(getActionSchema(step.action_type))
                  .sort(([a], [b]) => {
                    const order = (k) => ["body", "message"].includes(k) ? 2 : k === "subject" ? 1 : 0;
                    return order(a) - order(b);
                  })
                  .map(([key, prop]) => {
                  const resource = getResourceType(key);
                  const options = resource ? dynamicOptions[resource] : null;
                  const isLoading = resource ? loadingOptions[resource] : false;
                  const isFullWidth = ["body", "message", "subject"].includes(key);

                  return (
                    <Grid item xs={12} md={isFullWidth ? 12 : 6} key={key}>
                      {prop.enum ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>{prop.description || key}</InputLabel>
                          <Select
                            value={step.action_config?.[key] || ""}
                            label={prop.description || key}
                            onChange={(e) =>
                              updateStep(index, "action_config", {
                                ...step.action_config,
                                [key]: e.target.value,
                              })
                            }
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {prop.enum.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : isDynamicParam(key) && options ? (
                        <FormControl fullWidth size="small">
                          <InputLabel>{prop.description || key}</InputLabel>
                          <Select
                            value={step.action_config?.[key] || ""}
                            label={prop.description || key}
                            onChange={(e) =>
                              updateStep(index, "action_config", {
                                ...step.action_config,
                                [key]: e.target.value,
                              })
                            }
                            endAdornment={isLoading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
                          >
                            <MenuItem value="">
                              <em>Select {resource?.replace(/s$/, '') || key}</em>
                            </MenuItem>
                            {index > 0 && (
                              <MenuItem value={`{{prev_output.${key}}}`}>
                                <em>Use previous step output: {`{{prev_output.${key}}}`}</em>
                              </MenuItem>
                            )}
                            {options.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label} {opt.status ? `(${opt.status})` : ""} {opt.email ? `- ${opt.email}` : ""}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : key === "body" ? (
                        <BodyEditorField
                          label={prop.description || "Email body (HTML supported)"}
                          value={step.action_config?.[key] ?? ""}
                          onChange={(html) =>
                            updateStep(index, "action_config", {
                              ...step.action_config,
                              [key]: html,
                            })
                          }
                        />
                      ) : (
                        <TextField
                          label={prop.description || key}
                          value={step.action_config?.[key] ?? ""}
                          onChange={(e) =>
                            updateStep(index, "action_config", {
                              ...step.action_config,
                              [key]: prop.type === "integer" || prop.type === "number"
                                ? parseInt(e.target.value) || ""
                                : e.target.value,
                            })
                          }
                          fullWidth
                          size="small"
                          type={prop.type === "integer" || prop.type === "number" ? "number" : "text"}
                          multiline={["message"].includes(key)}
                          minRows={["message"].includes(key) ? 3 : undefined}
                          helperText={
                            prop.type === "string"
                              ? "Use {{prev_output.field}} or {{prev_output.items[0].id}} to reference previous step output"
                              : ""
                          }
                        />
                      )}
                    </Grid>
                  );
                })}

              {/* Condition */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                  Condition (optional) - Skip this step if condition is not met
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    label="Field"
                    placeholder="prev_output.success"
                    value={step.condition?.field || ""}
                    onChange={(e) =>
                      updateStep(index, "condition", {
                        ...(step.condition || {}),
                        field: e.target.value,
                      })
                    }
                    size="small"
                    sx={{ flex: 2 }}
                  />
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Op</InputLabel>
                    <Select
                      value={step.condition?.op || "eq"}
                      label="Op"
                      onChange={(e) =>
                        updateStep(index, "condition", {
                          ...(step.condition || {}),
                          op: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="eq">equals</MenuItem>
                      <MenuItem value="neq">not equals</MenuItem>
                      <MenuItem value="gt">greater than</MenuItem>
                      <MenuItem value="lt">less than</MenuItem>
                      <MenuItem value="contains">contains</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Value"
                    value={step.condition?.value ?? ""}
                    onChange={(e) =>
                      updateStep(index, "condition", {
                        ...(step.condition || {}),
                        value: e.target.value,
                      })
                    }
                    size="small"
                    sx={{ flex: 2 }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                  <Button size="small" onClick={() => moveStep(index, -1)} disabled={index === 0}>
                    Move Up
                  </Button>
                  <Button size="small" onClick={() => moveStep(index, 1)} disabled={index === steps.length - 1}>
                    Move Down
                  </Button>
                  <IconButton color="error" onClick={() => removeStep(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      ))}

      <Button variant="outlined" startIcon={<AddIcon />} onClick={addStep} sx={{ mt: 2 }}>
        Add Step
      </Button>
    </Box>
  );
}
